import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, provide } from 'vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { OfferLocation } from '@/helpers/offer-helper';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';
import { useMounted } from '@vueuse/core';
import type { BuyboxRegularOffer, UpcomingReleaseBuybox } from './types';

import { SnowplowClickoutContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

import { trackFreeTrialSwiperExp } from '../experiments/FreeTrialSwiperExp/tracking';
import { TitleOfferFragment } from '../buybox/graphql/fragments/Offer.fragment';
import { useBuyboxLoggedInExp } from '../experiments/BuyboxLoggedInExp/composables/useBuyboxLoggedInExp';
import { qualityTVProviderConfig } from '@/components/buybox/QualityTvRow/data';
import { useFewerPromotedTagsExpVariant } from '../experiments/FewerPromotedTagsExp';
import { useAppleAmzChannelExp } from '../experiments/AppleAmzChannelExp';


export default /*#__PURE__*/_defineComponent({
  __name: 'RegularBuyBoxNew',
  props: {
    regularOffers: null,
    allOffers: null,
    filter: null,
    title: null,
    additionalContexts: null,
    upcomingReleases: null,
    isInWatchlist: { type: Boolean },
    seasonNumber: null,
    episodeNumber: null,
    isEpisodeBuybox: { type: Boolean },
    promotedOffers: null
  },
  emits: ["onNotifyMe"],
  setup(__props: any) {

const props = __props as {
	regularOffers: BuyboxRegularOffer[];
	allOffers: TitleOfferFragment[];
	filter: BuyBoxFilterOptions;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	upcomingReleases: UpcomingReleaseBuybox[];
	isInWatchlist: boolean;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	isEpisodeBuybox?: boolean;
	// FEWER_PROMOTED_TAGS_EXP
	promotedOffers?: TitleOfferFragment[];
	// FEWER_PROMOTED_TAGS_EXP
};

const BuyBoxUpcomingNew = () => import('@/components/buybox-new/BuyBoxUpcomingNew.vue');
const BuyBoxOfferNew = () => import('@/components/buybox-new/BuyBoxOfferNew.vue');
const BuyBoxNoOffersNew = () => import('@/components/buybox-new/BuyBoxNoOffersNew.vue');
const BuyBoxNotifyMeNew = () => import('@/components/buybox-new/BuyBoxNotifyMeNew.vue');
const BuyBoxPromotionRowNew = () => import('@/components/buybox-new/BuyBoxPromotionRowNew.vue');
const PromotedBuyBoxNew = () => import('@/components/buybox-new/PromotedBuyBoxNew.vue');





const { language } = useLanguageStore();

const buyboxContainer = ref<HTMLElement | null>(null);
provide('offerLocation', OfferLocation.REGULAR_BUYBOX);

const showFreeTrial = computed(
	() =>
		!!promotionOffer.value &&
		[BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.FREE, BuyBoxFilterOptions.SUBSCRIPTION].includes(props.filter) &&
		// splitting language for locale specific ones like Spanish - Spain, Spanish - Latin America, etc
		allowedPromotionLanguages.includes(language.value.split('-')[0])
);

const hasFreeTrialOffers = computed(() => props?.regularOffers.length > 0 || inPromotionCatalogue.value);

const showNoOffers = computed(() => props.regularOffers.length === 0);

const showNotifyMeNew = computed(() => {
	return props.filter === BuyBoxFilterOptions.FREE ? hasFreeTrialOffers.value : !showNoOffers.value;
});

const hasOnlyUpcomingOffers = computed(() => props.upcomingReleases.length > 0 && showNoOffers.value);

// APPLE_AMZ_EXP
const { appleAmzChannelExpVariant, sortAndFilterAppleAmzChannelExpOffers } = useAppleAmzChannelExp(() => props.title);
// APPLE_AMZ_EXP

//  Quality TV
const qualityTvTrackingContexts = computed(() => {
	const titleObjectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;
	const titleObjectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const titleContext = new SnowplowTitleContextGraphql(
		titleObjectId,
		titleObjectType,
		props.seasonNumber ?? null,
		props.episodeNumber ?? null
	);

	return (props.additionalContexts || []).concat([titleContext]);
});
// Quality TV

const { promotionOffer, inPromotionCatalogue, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => props.allOffers,
});

const includeTVODQTVonRentOrBuy = computed(() => {
	// Include TVOD QTV (which acts as a 'freeTrial' offer) also on Rent and Buy filters. Other free trial offers are not included.
	return (
		catalogueItem.value?.placement === 'qtv' &&
		qualityTVProviderConfig[catalogueItem.value.packageId] &&
		qualityTVProviderConfig[catalogueItem.value.packageId].type === 'TVOD' &&
		[BuyBoxFilterOptions.BUY, BuyBoxFilterOptions.RENT].includes(props.filter)
	);
});

const _allOffers = computed(() => {
	let offers = [...props.regularOffers];

	// APPLE_AMZ_EXP
	if (appleAmzChannelExpVariant.value && appleAmzChannelExpVariant.value !== 'control') {
		offers = sortAndFilterAppleAmzChannelExpOffers(offers);
	}
	// APPLE_AMZ_EXP

	// Add promotion offer to the list of offers
	if (
		!showNoOffers.value &&
		([BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.SUBSCRIPTION, BuyBoxFilterOptions.FREE].includes(props.filter) ||
			includeTVODQTVonRentOrBuy.value) &&
		!!promotionOffer.value
	) {
		if (offers.length > 2) {
			offers.splice(2, 0, { ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer);
		} else {
			offers.push({ ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer);
		}
	}

	// FEWER_PROMOTED_TAGS_EXP
	// filter out promotedOffers from all offers
	if (fewerPromoExpVariant.value === 'variant_2' && isMounted.value) {
		offers = offers.filter(
			item =>
				item.rowType === 'freeTrial' ||
				!(filteredPromotedOffers.value || []).find(subItem => subItem.id === item.id)
		);
		// in this variant, we need to sort the free trial, to be always on 3rd row, including the promoted offers
		const targetFreeTrialIndex = 2 - (filteredPromotedOffers.value || []).length;
		const freeTrialIndex = offers.findIndex(offer => offer.rowType === 'freeTrial');
		[offers[targetFreeTrialIndex], offers[freeTrialIndex]] = [offers[freeTrialIndex], offers[targetFreeTrialIndex]];
	}
	// FEWER_PROMOTED_TAGS_EXP

	return offers.filter(Boolean);
});

// FEWER_PROMOTED_TAGS_EXP
const isMounted = useMounted();
const { variant: fewerPromoExpVariant } = useFewerPromotedTagsExpVariant(props.title.objectType, props.title.offers);
const isPromotionRowShown = computed(() => {
	if (fewerPromoExpVariant.value === 'variant_1') {
		return props.isEpisodeBuybox;
	}
	return true;
});
const forceQualityTv = computed(() => {
	if (fewerPromoExpVariant.value === 'variant_4') {
		return props.isEpisodeBuybox;
	}
	return false;
});
const fewerPromotionExpMergedBuyboxes = computed(() => {
	return fewerPromoExpVariant.value === 'variant_2';
});
const filteredPromotedOffers = computed(() => {
	// if we have filtered the buybox, we also filter the promoted offers accordingly
	return (props.promotedOffers || []).filter(
		offer =>
			props.filter === BuyBoxFilterOptions.ALL || offer.monetizationType === (props.filter as any).toLowerCase()
	);
});
const hasPromotedOffers = computed(() => (filteredPromotedOffers.value || []).length);
// FEWER_PROMOTED_TAGS_EXP

// BUYBOX_LOGGED_IN_EXP
const { trackBuyboxLoggedInExp } = useBuyboxLoggedInExp();
//BUYBOX_LOGGED_IN_EXP
function clickOut(payload: {
	buyboxOffer: BuyboxRegularOffer;
	isLeavingIconShown: boolean;
	row: HTMLElement | null;
	offerLocation?: OfferLocation;
}) {
	let _offer = payload.buyboxOffer;
	const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(_offer);

	// FT_SWIPER_EXP
	if (!inPromotionCatalogue.value) {
		const prefix =
			payload.offerLocation === OfferLocation.FREE_TRIAL
				? 'free_trial_clickout_' + (_offer.package.packageId === 350 ? 'atp' : 'amp') + '_'
				: 'clickout_';
		trackFreeTrialSwiperExp({
			action: 'click',
			label: props.isEpisodeBuybox ? prefix + 'episode' : prefix + 'buybox',
			contexts: [clickoutContext, ...props.additionalContexts],
		});
	}
	// FT_SWIPER_EXP
	const label =
		payload.offerLocation === OfferLocation.FREE_TRIAL ? 'free_trial_clickout_new_buybox' : 'clickout_new_buybox';
	trackBuyboxLoggedInExp({
		action: 'click',
		label: props.isEpisodeBuybox ? label + '_episode' : label,
		contexts: [clickoutContext],
	});
}

return { __sfc: true,BuyBoxUpcomingNew, BuyBoxOfferNew, BuyBoxNoOffersNew, BuyBoxNotifyMeNew, BuyBoxPromotionRowNew, PromotedBuyBoxNew, props, language, buyboxContainer, showFreeTrial, hasFreeTrialOffers, showNoOffers, showNotifyMeNew, hasOnlyUpcomingOffers, appleAmzChannelExpVariant, sortAndFilterAppleAmzChannelExpOffers, qualityTvTrackingContexts, promotionOffer, inPromotionCatalogue, catalogueItem, includeTVODQTVonRentOrBuy, _allOffers, isMounted, fewerPromoExpVariant, isPromotionRowShown, forceQualityTv, fewerPromotionExpMergedBuyboxes, filteredPromotedOffers, hasPromotedOffers, trackBuyboxLoggedInExp, clickOut }
}

})